import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import {
  EMPTY_REPORT,
  getReportDataThunk,
  getReportHeadersThunk,
  setHeaders,
  setReportData,
  setStatusData,
  setStatusHeaders,
} from '../../redux/slices/Report/reportSlice'
import { fetchEnabledTenants } from '../../services/tenantService'
import {
  getOptionsSpecies,
  getZoneTypesAsFilterOptions,
  getSiteOptionsByTenant,
  getAllReportData,
} from '../../services'
import { fetchEnabledParroquias } from '../../services/parroquiaService'
import GenerateReportComponent from './GenerateReportComponent'
import { exportReportToXlsx } from './exportReportToXlsx'
import LoaderContainer from '../../components/Loader/LoaderComponent'

export default function GenerateReportContainer() {
  const dispatch = useDispatch()
  const TOKEN = `Bearer ${window.localStorage.getItem('ArbolEc_token')}`

  const headers = useSelector(state => state.report.headers)
  const reportData = useSelector(state => state.report.reportData)
  const loadingReportHeaders = useSelector(state => state.report.statusHeaders)
  const loadingReportData = useSelector(state => state.report.statusData)

  // Tenant
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [tenants, setTenants] = useState([])
  const [tenantsLoading, setTenantsLoading] = useState(true)
  const [tenantsError, setTenantsError] = useState(false)

  // Toggle habilitado
  const [habilitadoFilter, setHabilitadoFilter] = useState('true')

  // Filtros adicionales — valores seleccionados
  const [especieSelected, setEspecieSelected] = useState(null)
  const [parroquiaSelected, setParroquiaSelected] = useState(null)
  const [tipoZonaSelected, setTipoZonaSelected] = useState(null)
  const [sitioSelected, setSitioSelected] = useState(null)

  // Filtros adicionales — opciones
  const [especieOptions, setEspecieOptions] = useState([])
  const [parroquiaOptions, setParroquiaOptions] = useState([])
  const [tipoZonaOptions, setTipoZonaOptions] = useState([])
  const [sitioOptions, setSitioOptions] = useState([])
  const [sitioOptionsLoading, setSitioOptionsLoading] = useState(false)

  // Paginación
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  // Descarga completa (todas las páginas)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState('')

  const currentRequestRef = useRef(null)

  useEffect(() => {
    if (headers.length <= 0) dispatch(getReportHeadersThunk(TOKEN))

    // Opciones globales (independientes del tenant)
    getOptionsSpecies().then(setEspecieOptions).catch(() => setEspecieOptions([]))
    fetchEnabledParroquias()
      .then(data => setParroquiaOptions(data.map(p => ({ id: p.id, title: p.nombre }))))
      .catch(() => setParroquiaOptions([]))
    getZoneTypesAsFilterOptions().then(setTipoZonaOptions).catch(() => setTipoZonaOptions([]))

    let active = true
    fetchEnabledTenants()
      .then(data => { if (active) setTenants(data) })
      .catch(() => { if (active) setTenantsError(true) })
      .finally(() => { if (active) setTenantsLoading(false) })

    window.history.pushState(null, null, window.location.href)
    const handlePopState = () => window.history.go(1)
    const handleKeyDown = e => {
      if ((e.ctrlKey && e.key === 'r') || e.key === 'F5') e.preventDefault()
    }
    const handleBeforeUnload = e => {
      e.preventDefault()
      e.returnValue = '¿Estás seguro de que deseas abandonar esta página?'
    }

    window.onpopstate = handlePopState
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      active = false
      currentRequestRef.current?.abort()
      window.onpopstate = null
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      dispatch(setStatusData(''))
      dispatch(setStatusHeaders(''))
      dispatch(setReportData(EMPTY_REPORT))
      dispatch(setHeaders([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchReportData = ({ tenantId, currentPage, limit, habilitado, especieId = null, parroquiaId = null, tipoZonaId = null, sitioId = null }) => {
    if (currentRequestRef.current) currentRequestRef.current.abort()
    const request = dispatch(
      getReportDataThunk({ tenantId, page: currentPage + 1, limit, habilitado, especieId, parroquiaId, tipoZonaId, sitioId })
    )
    currentRequestRef.current = request
    request.finally(() => {
      if (currentRequestRef.current === request) currentRequestRef.current = null
    })
  }

  // Lee el estado actual de los filtros — para no pisar valores cuando cambia solo uno
  const currentFilterIds = () => ({
    especieId: especieSelected?.id ?? null,
    parroquiaId: parroquiaSelected?.id ?? null,
    tipoZonaId: tipoZonaSelected?.id ?? null,
    sitioId: sitioSelected?.id ?? null,
  })

  const resetFilterSelections = () => {
    setEspecieSelected(null)
    setParroquiaSelected(null)
    setTipoZonaSelected(null)
    setSitioSelected(null)
  }

  const handleTenantChange = (_, tenant) => {
    setSelectedTenant(tenant)
    setPage(0)
    resetFilterSelections()
    setSitioOptions([])
    dispatch(setReportData(EMPTY_REPORT))
    if (tenant) {
      setSitioOptionsLoading(true)
      getSiteOptionsByTenant(tenant.id)
        .then(setSitioOptions)
        .finally(() => setSitioOptionsLoading(false))
      fetchReportData({ tenantId: tenant.id, currentPage: 0, limit: rowsPerPage, habilitado: habilitadoFilter })
    } else {
      dispatch(setStatusData(''))
    }
  }

  const handleHabilitadoChange = (_, value) => {
    if (!value || !selectedTenant) return
    setHabilitadoFilter(value)
    setPage(0)
    dispatch(setReportData(EMPTY_REPORT))
    fetchReportData({ tenantId: selectedTenant.id, currentPage: 0, limit: rowsPerPage, habilitado: value, ...currentFilterIds() })
  }

  const handleEspecieChange = (_, value) => {
    setEspecieSelected(value)
    if (!selectedTenant) return
    setPage(0)
    dispatch(setReportData(EMPTY_REPORT))
    fetchReportData({ tenantId: selectedTenant.id, currentPage: 0, limit: rowsPerPage, habilitado: habilitadoFilter, ...currentFilterIds(), especieId: value?.id ?? null })
  }

  const handleParroquiaChange = (_, value) => {
    setParroquiaSelected(value)
    if (!selectedTenant) return
    setPage(0)
    dispatch(setReportData(EMPTY_REPORT))
    fetchReportData({ tenantId: selectedTenant.id, currentPage: 0, limit: rowsPerPage, habilitado: habilitadoFilter, ...currentFilterIds(), parroquiaId: value?.id ?? null })
  }

  const handleTipoZonaChange = (_, value) => {
    setTipoZonaSelected(value)
    if (!selectedTenant) return
    setPage(0)
    dispatch(setReportData(EMPTY_REPORT))
    fetchReportData({ tenantId: selectedTenant.id, currentPage: 0, limit: rowsPerPage, habilitado: habilitadoFilter, ...currentFilterIds(), tipoZonaId: value?.id ?? null })
  }

  const handleSitioChange = (_, value) => {
    setSitioSelected(value)
    if (!selectedTenant) return
    setPage(0)
    dispatch(setReportData(EMPTY_REPORT))
    fetchReportData({ tenantId: selectedTenant.id, currentPage: 0, limit: rowsPerPage, habilitado: habilitadoFilter, ...currentFilterIds(), sitioId: value?.id ?? null })
  }

  const handleDownloadAll = async () => {
    if (!selectedTenant || downloading) return
    setDownloading(true)
    setDownloadError('')
    try {
      const { resultALL } = await getAllReportData({
        tenantId: selectedTenant.id,
        habilitado: habilitadoFilter,
        ...currentFilterIds(),
      })
      if (!resultALL || resultALL.length === 0) {
        setDownloadError('No hay datos para descargar con los filtros seleccionados.')
        return
      }
      exportReportToXlsx(resultALL, selectedTenant)
    } catch (error) {
      setDownloadError('No se pudo generar la descarga. Inténtalo nuevamente.')
    } finally {
      setDownloading(false)
    }
  }

  const handleTableChange = (action, tableState) => {
    if (!selectedTenant) return
    if (action === 'changePage') {
      setPage(tableState.page)
      fetchReportData({ tenantId: selectedTenant.id, currentPage: tableState.page, limit: rowsPerPage, habilitado: habilitadoFilter, ...currentFilterIds() })
    }
    if (action === 'changeRowsPerPage') {
      setRowsPerPage(tableState.rowsPerPage)
      setPage(0)
      fetchReportData({ tenantId: selectedTenant.id, currentPage: 0, limit: tableState.rowsPerPage, habilitado: habilitadoFilter, ...currentFilterIds() })
    }
  }

  const filtersDisabled = !selectedTenant

  return (
    <Box
      sx={{
        height: 'calc(100vh - 6.2vh)',
        width: '100vw',
        mt: '6.2vh',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
      }}>
      <LoaderContainer
        conditions={[loadingReportHeaders === 'loading', loadingReportData === 'loading']}
      />
      <Paper
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        square>
        {/* Fila 1: Tenant + Toggle habilitado */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: 'wrap',
          }}>
          {tenantsError ? (
            <Typography variant='body2' color='error'>
              No se pudieron cargar los tenants. Recarga la página.
            </Typography>
          ) : (
            <Autocomplete
              options={tenants}
              value={selectedTenant}
              onChange={handleTenantChange}
              loading={tenantsLoading}
              getOptionLabel={t => t?.fullName || t?.name || t?.slug || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Tenant'
                  size='small'
                  sx={{ width: 300 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {tenantsLoading && <CircularProgress color='inherit' size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          <ToggleButtonGroup
            value={habilitadoFilter}
            exclusive
            onChange={handleHabilitadoChange}
            size='small'
            disabled={filtersDisabled}>
            <ToggleButton value='true'>Habilitados</ToggleButton>
            <ToggleButton value='all'>Todos</ToggleButton>
            <ToggleButton value='false'>No habilitados</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Fila 2: Filtros adicionales */}
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}>
          <Autocomplete
            options={especieOptions}
            value={especieSelected}
            onChange={handleEspecieChange}
            disabled={filtersDisabled}
            getOptionLabel={o => o?.title || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={params => (
              <TextField {...params} label='Especie' size='small' sx={{ width: 220 }} />
            )}
          />
          <Autocomplete
            options={parroquiaOptions}
            value={parroquiaSelected}
            onChange={handleParroquiaChange}
            disabled={filtersDisabled}
            getOptionLabel={o => o?.title || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={params => (
              <TextField {...params} label='Parroquia' size='small' sx={{ width: 200 }} />
            )}
          />
          <Autocomplete
            options={tipoZonaOptions}
            value={tipoZonaSelected}
            onChange={handleTipoZonaChange}
            disabled={filtersDisabled}
            getOptionLabel={o => o?.title || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={params => (
              <TextField {...params} label='Contexto vial' size='small' sx={{ width: 200 }} />
            )}
          />
          <Autocomplete
            options={sitioOptions}
            value={sitioSelected}
            onChange={handleSitioChange}
            loading={sitioOptionsLoading}
            disabled={filtersDisabled}
            getOptionLabel={o => o?.title || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={params => (
              <TextField
                {...params}
                label='Sitio'
                size='small'
                sx={{ width: 220 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {sitioOptionsLoading && <CircularProgress color='inherit' size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <GenerateReportComponent
            headers={headers}
            reportData={reportData}
            selectedTenant={selectedTenant}
            page={page}
            rowsPerPage={rowsPerPage}
            onTableChange={handleTableChange}
            onDownloadAll={handleDownloadAll}
            downloading={downloading}
          />
        </Box>
      </Paper>
      <Snackbar
        open={Boolean(downloadError)}
        autoHideDuration={6000}
        onClose={() => setDownloadError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity='error' variant='filled' onClose={() => setDownloadError('')}>
          {downloadError}
        </Alert>
      </Snackbar>
    </Box>
  )
}
